// @givmo/design-system — FilterBar + FilterChip (SaaS layer).
//
// The toolbar filter layer DESIGN-SAAS.md item 10 anticipates ("Tag/Filter chip as needed"): a row of
// toggleable chips for a table toolbar (the approvals queue) or a report scope picker.
//
// `rounded-md`, NOT a consumer pill — the design language bans pills on dashboard chrome. Unselected is
// the `secondary` button recipe (hairline `border-strong` on `surface`); selected is a brand tint with a
// brand border, so selection reads without relying on color alone at a glance.
//
// a11y — the load-bearing part:
//   - A toggle chip is a real `<button aria-pressed>`, so it is keyboard-reachable and its state is
//     ANNOUNCED. Selection is never conveyed by color alone.
//   - A removable chip's `×` is its OWN sibling button with its own `aria-label`, never nested inside
//     the toggle button: nested interactive elements are invalid HTML and leave keyboard users unable
//     to clear a filter without also toggling it.
//   - `FilterBar` is a `role="group"` with an accessible name, so a screen reader announces the set as
//     a group instead of loose buttons.
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

const CHIP_BASE = [
  'inline-flex h-28 items-center gap-6 rounded-md px-10',
  'font-sans text-s13 font-w500 tracking-normal',
  'outline-none transition-colors focus-visible:shadow-ring',
];

const CHIP_UNSELECTED =
  'border border-border-strong bg-surface text-text-secondary hover:border-neutral-400 hover:bg-surface-hover active:bg-neutral-150';
const CHIP_SELECTED =
  'border border-brand-500 bg-brand-50 text-brand-700 hover:bg-brand-100 active:bg-brand-100';
const CHIP_DISABLED =
  'disabled:border-border-subtle disabled:bg-neutral-50 disabled:text-neutral-400 disabled:hover:bg-neutral-50';

export interface FilterChipProps extends TestIdProps {
  label: ReactNode;
  /** Selected/active state — drives the brand tint AND `aria-pressed`. */
  selected?: boolean;
  /** Optional trailing count (e.g. how many rows match this filter). */
  count?: number;
  /** Toggle handler. Omit for a read-only chip (rendered as a plain span, not a button). */
  onToggle?: () => void;
  /** Adds a sibling `×` button that clears this filter. */
  onRemove?: () => void;
  /** Accessible label for the `×` (defaults to "Remove {label} filter" when label is a string). */
  removeLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function FilterChip({
  label,
  selected = false,
  count,
  onToggle,
  onRemove,
  removeLabel,
  disabled = false,
  className,
  testId,
}: FilterChipProps) {
  const body = (
    <>
      {label}
      {count != null && <span className="tabular-nums opacity-70">{count}</span>}
    </>
  );

  const chipClass = cn(
    CHIP_BASE,
    selected ? CHIP_SELECTED : CHIP_UNSELECTED,
    onToggle != null && CHIP_DISABLED,
    className,
  );

  const resolvedRemoveLabel =
    removeLabel ?? (typeof label === 'string' ? `Remove ${label} filter` : 'Remove filter');

  // A `×` alongside a toggle needs TWO sibling buttons — never a button inside a button.
  const removeButton =
    onRemove != null ? (
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={resolvedRemoveLabel}
        className={cn(
          'inline-flex size-20 shrink-0 items-center justify-center rounded-xs',
          'text-icon-muted outline-none transition-colors',
          'hover:bg-neutral-200 hover:text-text-strong focus-visible:shadow-ring',
          'disabled:text-neutral-300 disabled:hover:bg-transparent',
        )}
        {...testIdProp(testId ? `${testId}-remove` : undefined)}
      >
        <Icon icon={X} size={12} aria-hidden />
      </button>
    ) : null;

  if (onToggle == null) {
    // Read-only chip: no button semantics, because there is nothing to activate.
    return (
      <span className={cn('inline-flex items-center gap-4')}>
        <span className={chipClass} {...testIdProp(testId)}>
          {body}
        </span>
        {removeButton}
      </span>
    );
  }

  if (removeButton == null) {
    return (
      <button
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onToggle}
        className={chipClass}
        {...testIdProp(testId)}
      >
        {body}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-4">
      <button
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onToggle}
        className={chipClass}
        {...testIdProp(testId)}
      >
        {body}
      </button>
      {removeButton}
    </span>
  );
}

export interface FilterBarProps extends TestIdProps {
  /** Visible leading label (e.g. "Filter"). Also the group's accessible name when present. */
  label?: ReactNode;
  /** Accessible name for the group when there is no visible `label` (or it isn't a string). */
  ariaLabel?: string;
  /** The chips / controls. */
  children: ReactNode;
  /** Renders a trailing text button that clears every filter. */
  onClearAll?: () => void;
  clearAllLabel?: string;
  className?: string;
}

export function FilterBar({
  label,
  ariaLabel,
  children,
  onClearAll,
  clearAllLabel = 'Clear all',
  className,
  testId,
}: FilterBarProps) {
  const groupName = ariaLabel ?? (typeof label === 'string' ? label : 'Filters');

  return (
    <div
      role="group"
      aria-label={groupName}
      className={cn('flex flex-wrap items-center gap-8', className)}
      {...testIdProp(testId)}
    >
      {label != null && (
        <span className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
          {label}
        </span>
      )}
      {children}
      {onClearAll != null && (
        <button
          type="button"
          onClick={onClearAll}
          className={cn(
            'font-sans text-s13 font-w600 tracking-givmo text-brand-600',
            'outline-none hover:underline focus-visible:underline',
          )}
          {...testIdProp(testId ? `${testId}-clear` : undefined)}
        >
          {clearAllLabel}
        </button>
      )}
    </div>
  );
}
